import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

// Global "Admin Mode". Two rules:
//  1. A non-admin account can NEVER be in admin mode — `adminMode` is forced
//     false unless the signed-in user has admin access (role-gated at the source,
//     so it overrides any stale localStorage value on shared/other devices).
//  2. For admins it defaults OFF and is only on when they explicitly turn it on
//     (persisted per device). Individual pages still expose a local view toggle.
const Ctx = createContext(null);

export function AdminModeProvider({ children }) {
    const auth = useAuth() || {};
    const hasAdminAccess = !!auth.hasAdminAccess;
    const isOwner = !!auth.isOwner;
    const [pref, setPref] = useState(() => {
        try { return localStorage.getItem("rosterly_admin_mode") === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem("rosterly_admin_mode", pref ? "1" : "0"); } catch { /* ignore */ }
    }, [pref]);
    // Owner View — the owner-only edit mode (nav re-order, milestone tiers, etc).
    const [ownerPref, setOwnerPref] = useState(() => {
        try { return localStorage.getItem("rosterly_owner_view") === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem("rosterly_owner_view", ownerPref ? "1" : "0"); } catch { /* ignore */ }
    }, [ownerPref]);
    const ownerView = isOwner && ownerPref;
    // Owner-only: reveal every page/access toggle button (right-click / long-press My Account).
    const [allPref, setAllPref] = useState(() => { try { return localStorage.getItem("rosterly_all_buttons") === "1"; } catch { return false; } });
    useEffect(() => { try { localStorage.setItem("rosterly_all_buttons", allPref ? "1" : "0"); } catch { /* ignore */ } }, [allPref]);
    const allButtons = isOwner && allPref;
    const toggleAllButtons = () => setAllPref((v) => !v);
    const setOwnerView = (v) => setOwnerPref((prev) => (typeof v === "function" ? v(prev) : !!v));
    const toggleOwnerView = () => setOwnerPref((v) => !v);

    // Effective admin mode: only true for admin-capable accounts that opted in.
    const adminMode = hasAdminAccess && pref;
    const setAdminMode = (v) => setPref((prev) => (typeof v === "function" ? v(prev) : !!v));

    return (
        <Ctx.Provider value={{ adminMode, setAdminMode, toggle: () => setPref((v) => !v), ownerView, setOwnerView, toggleOwnerView, allButtons, toggleAllButtons }}>
            {children}
        </Ctx.Provider>
    );
}

export function useAdminMode() {
    return useContext(Ctx) || { adminMode: false, setAdminMode: () => {}, toggle: () => {}, ownerView: false, setOwnerView: () => {}, toggleOwnerView: () => {}, allButtons: false, toggleAllButtons: () => {} };
}
